'use client';

import { Box, Button, Sheet, Stack, Typography, useBreakpoints } from '@castlery/fortress';
import {
  Product,
  selectBundleVariants,
  selectCurrentProductStockState,
  selectDiscontinued,
  selectCurrentWarrantyOffer,
  selectLeadtimeShippingFee,
  selectProduct,
  selectVariant,
  Variant,
} from '@castlery/modules-product-domain';
import { webAddToCartCommand } from '@castlery/modules-product-services';
import { useAppDispatch, useAppSelector } from '@castlery/shared-redux-store';
import { STOCK_STATE } from '@castlery/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAsyncFn } from 'react-use';
import { usePrice } from '../../hooks/use-price';
import { ProductCartToast } from './components/product-cart-toast';
import { ProductLongLeadTimeModal } from './components/product-long-lead-time-modal';
import { EVENT_MULBERRY_WARRANTY } from '@castlery/modules-tracking-services';

export const ProductAddToCart = () => {
  const dispatch = useAppDispatch();
  const stockState = useAppSelector(selectCurrentProductStockState);
  const leadtimeShippingFee = useAppSelector(selectLeadtimeShippingFee);
  const isOutOfStock = useMemo(() => stockState === STOCK_STATE.OUT_OF_STOCK, [stockState]);
  const discontinued = useAppSelector(selectDiscontinued);
  const product = useAppSelector(selectProduct) as Product;
  const variant = useAppSelector(selectVariant) as Variant;
  const bundleVariant = useAppSelector(selectBundleVariants);
  const currentWarrantyOffer = useAppSelector(selectCurrentWarrantyOffer);

  const { variantPrice, variantListPrice, numberVariantPrice, numberVariantListPrice } = usePrice({
    product,
    variant,
    bundleVariant,
  });
  const showSalePrice = useMemo(
    () => variantPrice && variantListPrice && variantPrice !== variantListPrice,
    [variantListPrice, variantPrice]
  );
  // const orderLoading = useAppSelector(selectOrderLoading);
  const { desktop, tablet, mobile } = useBreakpoints();
  const [isLongLeadTimeModalOpen, setIsLongLeadTimeModalOpen] = useState(false);
  const [isAddToCartToastOpen, setIsAddToCartToastOpen] = useState(false);
  const [showStickyButton, setShowStickyButton] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  const pageRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleTrackMulberryEvent = async (targetOffer: any) => {
    if (targetOffer) {
      await dispatch(
        EVENT_MULBERRY_WARRANTY({
          action: 'add_extended_warranty',
          label: `${targetOffer.duration_months ? Number(targetOffer.duration_months) / 12 : 0} Years`,
          sku: variant?.sku ?? '',
          skuName: variant?.product_name ?? '',
          position: 'pdp',
          price: targetOffer?.cost ?? '',
        })
      );
    }
  };

  const [
    {
      loading: atcLoading,
      //  error
    },
    addToOrder,
  ] = useAsyncFn(async () => {
    try {
      await dispatch(webAddToCartCommand({})).unwrap();
      setIsLongLeadTimeModalOpen(false);
      setTimeout(() => {
        setIsAddToCartToastOpen(true);
      }, 0);
    } catch (error: Error | any) {
      console.log('🚀 ~ const[{loading:ATCLoading},addToOrder]=useAsyncFn ~ error:', error);
      return undefined;
    }
  });

  const handleAddToCartWithLongLeadTime = useCallback(
    async (e: any) => {
      e?.preventDefault();
      if (leadtimeShippingFee?.show_leadtime_explanation) {
        setIsLongLeadTimeModalOpen(true);
      } else {
        handleTrackMulberryEvent(currentWarrantyOffer);
        await addToOrder();
      }
    },
    [addToOrder, leadtimeShippingFee?.show_leadtime_explanation, currentWarrantyOffer]
  );

  useEffect(() => {
    if (!pageRef.current) {
      pageRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (!buttonRef.current || desktop) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShowStickyButton(false);
        } else {
          const rect = entry.boundingClientRect;
          const isDisappearingFromTop = rect.bottom < 50;
          const isDisappearingFromBottom = rect.top > window.innerHeight;
          if (isDisappearingFromTop) {
            setShowStickyButton(true);
          } else if (isDisappearingFromBottom) {
            setShowStickyButton(true);
          }
        }
      },
      {
        threshold: 0.1,
        rootMargin: '-50px 0px 0px 0px',
      }
    );

    observer.observe(buttonRef.current);

    return () => observer.disconnect();
  }, [desktop]);

  useEffect(() => {
    if (desktop) return;

    const footerElement = document.querySelector('footer[role="contentinfo"]');
    if (!footerElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterVisible(entry.isIntersecting);
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px 0px 0px',
      }
    );

    observer.observe(footerElement);

    return () => observer.disconnect();
  }, [desktop]);

  return (
    <>
      <Stack px={mobile ? 6 : tablet ? 6 : undefined}>
        <Button
          ref={buttonRef}
          variant="primary"
          data-selenium={desktop ? 'add_to_cart' : tablet ? 'add_to_cart_tablet' : 'add_to_cart_mobile'}
          disabled={discontinued || isOutOfStock}
          loading={atcLoading || !pageRef.current}
          onClick={handleAddToCartWithLongLeadTime}
          sx={{
            width: '100%',
            my: 4,
          }}
        >
          {!atcLoading && discontinued
            ? 'Unavailable'
            : isOutOfStock
            ? 'Out of Stock'
            : `Add To Cart - ${variantPrice}`}
        </Button>
      </Stack>

      {showStickyButton && !desktop && !isFooterVisible && (
        <Sheet
          variant="solid"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            px: 6,
            pt: 4,
            pb: 8,
            boxShadow: (theme) => theme.shadow.md,
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
            <Stack justifyContent="center" alignItems="flex-start" gap={1}>
              <Typography
                level="h4"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {product?.name}
              </Typography>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography
                  level="body2"
                  sx={{
                    ...(showSalePrice
                      ? {
                          fontWeight: 700,
                          color: 'var(--fortress-palette-brand-terracotta-500)',
                        }
                      : {
                          color: 'var(--fortress-palette-brand-maroonVelvet-500)',
                        }),
                  }}
                >
                  {variantPrice}
                </Typography>
                {showSalePrice && (
                  <Typography
                    level="body2"
                    sx={{
                      color: 'var(--fortress-palette-brand-mono-500)',
                      textDecoration: 'line-through',
                      ml: 1,
                    }}
                  >
                    {variantListPrice}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Button
              variant="primary"
              data-selenium={tablet ? 'add_to_cart_sticky_tablet' : 'add_to_cart_sticky_mobile'}
              disabled={discontinued || isOutOfStock}
              loading={atcLoading || !pageRef.current}
              onClick={handleAddToCartWithLongLeadTime}
              sx={{
                textWrap: 'nowrap',
              }}
            >
              {!atcLoading && discontinued ? 'Unavailable' : isOutOfStock ? 'Out of Stock' : `Add To Cart`}
            </Button>
          </Stack>
        </Sheet>
      )}

      <ProductLongLeadTimeModal
        variant={variant}
        open={isLongLeadTimeModalOpen}
        onClose={() => setIsLongLeadTimeModalOpen(false)}
        atcLoading={atcLoading}
        handleAddToCart={addToOrder}
      />
      <ProductCartToast
        open={isAddToCartToastOpen}
        onClose={() => setIsAddToCartToastOpen(false)}
        variant={variant}
        price={variantPrice}
        listPrice={variantListPrice}
        numberPrice={numberVariantPrice}
        numberListPrice={numberVariantListPrice}
      />
    </>
  );
};
